import { FC, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MeetingType,
  MeetingStepViewId,
  SkipMeetingStepReason,
  EmployeeDisplayTinyInfo,
  EmployeeWithBuFragment,
  AppraisalSeasonDto,
  EmployeeWithBusinessUnit,
  SkillEmployeeStatusDto,
} from '../../../types'
import { useAuth } from '../../../context/auth-context'
import useInviteMeeting, { MeetingInviteProps } from '../../../graphql/meeting/hooks/useInviteMeeting'
import { ValidationErrorsData } from '../../../shared/graphqlErrorHelper'
import { PerModal } from '../../../components/custom_components/Modal'
import { getSampleEmail, getNextStepConfiguration, fullAppraisalSteps, updateAppraisalSteps } from '../../meeting/invite/inviteUtil'
import moment from 'moment-timezone'
import { sortSteps } from '../../appraisal/appraisal-flow/meeting/stepSorter'
import { InviteModalContentUtils } from '../../../components/custom_components/InviteModalContent/inviteModalContentUtils'
import AppraisalInviteModalContent from './AppraisalInviteModalContent'

export interface AppraisalInviteModalState {
  meetingDate: string
  meetingType: MeetingType
  appraisalSeason: AppraisalSeasonDto
  emailText?: string
  viewIds?: MeetingStepViewId[]
  reasonForReducedSteps?: SkipMeetingStepReason
  serverError?: ValidationErrorsData[]
  location?: string
  duration: number
  additionalParticipants: EmployeeWithBuFragment[]
  additionalParticipantsOptions: EmployeeWithBuFragment[]
  skillStatusData: SkillEmployeeStatusDto | null
}

interface ReducerActionType {
  type: 'update' | 'updateSteps' | 'reset' | 'setError'
  fieldName?: string
  value?: unknown
  stepId?: MeetingStepViewId
  error?: ValidationErrorsData[]
  serverError?: null
  location?: string
}

interface AppraisalInviteModalProps {
  visible: boolean
  onCancel: () => void
  employee: EmployeeDisplayTinyInfo
  appraisalSeason: AppraisalSeasonDto
  additionalParticipantOptions: EmployeeWithBusinessUnit[]
  meetingType: MeetingType
  skillStatusData: SkillEmployeeStatusDto | null
}

export const AppraisalInviteModalPublicProfile: FC<AppraisalInviteModalProps> = ({
  visible,
  employee,
  onCancel,
  appraisalSeason,
  additionalParticipantOptions,
  meetingType,
  skillStatusData,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { createAppraisalInvitePublicProfile } = useInviteMeeting()
  // if next monday is still inside appraisal season, suggest that date. Else pick last day of appraisal season
  let suggestedDate = moment().tz(user.timezone).startOf('isoWeek').add(1, 'week').hours(8)
  if (suggestedDate.isAfter(appraisalSeason.toDate)) {
    suggestedDate = moment(appraisalSeason.toDate).tz(user.timezone).subtract(1, 'days').startOf('day').hours(8)
    // Adjust if the selected date is on a weekend (in case end of appraisal season has been selected)
    if (suggestedDate.isoWeekday() === 6 || suggestedDate.isoWeekday() === 7) {
      // Move back to the previous Friday
      suggestedDate = suggestedDate.isoWeekday(5)
    }
  }

  const suggestedDateUTCString = suggestedDate.tz('UTC').format('YYYY-MM-DDTHH:mm:00.000') + 'Z'
  const [employeeSkillStatus, setEmployeeSkillStatus] = useState<SkillEmployeeStatusDto | null>(skillStatusData)

  const initialState: AppraisalInviteModalState = {
    meetingDate: suggestedDateUTCString,
    serverError: undefined,
    emailText: getSampleEmail(t, meetingType, employee, user),
    viewIds: meetingType === MeetingType.Appraisal ? fullAppraisalSteps : updateAppraisalSteps,
    appraisalSeason: appraisalSeason,
    meetingType,
    reasonForReducedSteps: undefined,
    location: '',
    duration: 3600,
    additionalParticipantsOptions: additionalParticipantOptions,
    additionalParticipants: [],
    skillStatusData: employeeSkillStatus,
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  const reducer = (state: AppraisalInviteModalState, action: ReducerActionType) => {
    const nextState = { ...state }
    switch (action.type) {
      case 'reset':
        nextState.serverError = []
        setIsSubmitting(false)
        return initialState

      case 'update':
        const { fieldName, value } = action
        if (fieldName) {
          nextState[fieldName] = value
        }
        break

      case 'updateSteps':
        nextState.viewIds = getNextStepConfiguration(action.stepId!, state.meetingType, state.viewIds)
        break

      case 'setError':
        if (action.error) {
          nextState.serverError = action.error as ValidationErrorsData[]
        }
        break

      default:
        throw new Error()
    }
    return nextState
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  const saveInvitation = async () => {
    setIsSubmitting(true)
    const inviteData: MeetingInviteProps = {
      employeeId: employee.id,
      appraisalSeasonId: state.appraisalSeason!.id,
      reviewerId: user.employeeId,
      meetingDate: state.meetingDate,
      meetingType: state.meetingType,
      viewIds: sortSteps(state.viewIds ? state.viewIds : [], state.meetingType === MeetingType.AppraisalUpdate),
      emailText: state.emailText,
      reasonForReducedSteps: appraisalWithReducedSteps ? state.reasonForReducedSteps : undefined,
      location: state.location,
      duration: state.duration,
      additionalParticipantsIds: state.additionalParticipants.map((e) => e.id),
    }

    const result = await createAppraisalInvitePublicProfile(inviteData)

    if (result && !result.errors) {
      dispatch({ type: 'reset' })
      onCancel()
    } else {
      setIsSubmitting(false)
      dispatch({
        type: 'setError',
        error: result.errors && result.errors[0] && result.errors[0].extensions ? result.errors[0].extensions.validationErrors : undefined,
      })
    }
  }

  const containsStepViewId = (stepId: MeetingStepViewId): boolean => {
    if (state.viewIds) {
      return !!state.viewIds.find((x) => x === stepId)
    }
    return false
  }

  const isAppraisalMeeting = state.meetingType === MeetingType.Appraisal
  const isAppraisalUpdateMeeting = state.meetingType === MeetingType.AppraisalUpdate

  const appraisalWithReducedSteps =
    isAppraisalMeeting && !(containsStepViewId(MeetingStepViewId.SelfReflection) && containsStepViewId(MeetingStepViewId.Evaluation))

  const invalidAppraisal = appraisalWithReducedSteps && !state.reasonForReducedSteps
  const invalidAppraisalUpdate = isAppraisalUpdateMeeting && (!state.viewIds || state.viewIds.length === 0)

  const inviteModalContentUtils = new InviteModalContentUtils({
    timeZoneAndLocale: user,
    appraisalSeason: appraisalSeason,
  })
  const invalidDateTime =
    state.meetingDate === '' ||
    state.meetingDate === null ||
    state.meetingDate === undefined ||
    inviteModalContentUtils.isMeetingDateNotAllowed(state.meetingDate)

  const disabledInviteButton =
    invalidAppraisalUpdate || invalidAppraisal || isSubmitting || invalidDateTime || (state.serverError && state.serverError.length > 0)

  const disabledInviteButtonTooltip = () => {
    if (invalidDateTime) {
      return t(`meetingInvite.invalidDateTimeTooltip`)
    } else {
      return undefined
    }
  }

  const isSkillStatusBlocker = false

  return (
    <PerModal
      visible={visible}
      onCancel={() => {
        dispatch({ type: 'reset' })
        onCancel()
      }}
      size="large"
      title={{
        heading: t(`meetingInvite.headline`),
      }}
      footer={{
        justify: 'end',
        customBtns: [
          {
            text: t(`meetingInvite.doScheduleButton`),
            onClick: async (e) => {
              e.preventDefault() // We should refactor button to not emit native event!
              await saveInvitation()
            },
            color: 'primary',
            icon: 'calendar_fill',
            disabled: disabledInviteButton || isSkillStatusBlocker,
            tooltip: isSkillStatusBlocker ? t('meetingInvite.unknownSkillStatus') : disabledInviteButtonTooltip(),
            visible: !state.serverError,
            dataE2eAttr: 'invite',
          },
        ],
      }}
    >
      <>
        {visible && (
          <>
            <AppraisalInviteModalContent
              employee={employee}
              state={state}
              timelocal={user}
              inviteModalContentUtils={inviteModalContentUtils}
              onUpdate={(fieldName, value) => dispatch({ type: 'update', fieldName: fieldName, value: value })}
              onUpdateSteps={(stepId, value) => dispatch({ type: 'updateSteps', stepId: stepId, value: value })}
              requestedMeeting={undefined}
              setEmployeeSkillStatus={setEmployeeSkillStatus}
              employeeSkillStatus={employeeSkillStatus}
              serverError={state.serverError}
            />
          </>
        )}
      </>
    </PerModal>
  )
}
